import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { getUseCasesForLanguage } from '../../content/useCases'
import { useLanguage } from '../../i18n/LanguageContext'
import { trackLandingEvent, withStoreUtm } from '../../services/analytics'

const PageContainer = styled.main`
  padding: 50px 24px 80px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`

const Eyebrow = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-accent);
`

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  margin: 0;
  color: var(--color-text-primary);
`

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0;
  max-width: 780px;
  line-height: 1.7;
`

const CardsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
`

const Card = styled(Link)`
  text-decoration: none;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-medium);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: var(--shadow-soft);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-medium);
  }
`

const CardIndustry = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const CardTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
`

const CardExcerpt = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  color: var(--color-text-muted);
  font-size: 0.9rem;
`

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`

const Keyword = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-accent-soft);
  color: var(--color-accent);
  font-size: 0.9rem;
`

const TopCTASection = styled.section`
  background: linear-gradient(135deg, rgba(63, 118, 255, 0.12), rgba(140, 214, 255, 0.12));
  border: 1px solid var(--color-border);
  border-radius: var(--radius-large);
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  box-shadow: var(--shadow-soft);
`

const TopCTAHeading = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-text-primary);
`

const TopCTAText = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
`

const TopCTAButton = styled.a`
  padding: 12px 24px;
  border-radius: 999px;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 1rem;
  background: linear-gradient(135deg, #6366f1, #3b82f6);
  color: white;
  box-shadow: 0 20px 40px rgba(79, 70, 229, 0.25);

  &:hover {
    transform: translateY(-3px);
  }
`
const chromeWebStoreUrl = withStoreUtm('use_case_index_top')

const UseCaseIndex: React.FC = () => {
  const { language, t } = useLanguage()
  const localizedUseCases = getUseCasesForLanguage(language)

  return (
    <PageContainer>
      <TopCTASection>
        <TopCTAHeading>{t('useCases.ctaTitle')}</TopCTAHeading>
        <TopCTAText>{t('useCases.ctaText')}</TopCTAText>
        <TopCTAButton
          href={chromeWebStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLandingEvent('store_cta_click', { placement: 'use_case_index_top' })}
        >
          {t('useCases.ctaButton')}
        </TopCTAButton>
      </TopCTASection>
      <HeroSection>
        <Eyebrow>Use Case Library</Eyebrow>
        <Title>{t('useCases.indexTitle')}</Title>
        <Subtitle>{t('useCases.indexSubtitle')}</Subtitle>
      </HeroSection>
      <CardsGrid>
        {localizedUseCases.map((useCase) => (
          <Card
            key={useCase.slug}
            to={`/solutions/${useCase.slug}`}
            onClick={() => trackLandingEvent('use_case_click', { slug: useCase.slug })}
          >
            <CardIndustry>
              {useCase.heroEyebrow} · {useCase.industry}
            </CardIndustry>
            <CardTitle>{useCase.title}</CardTitle>
            <CardExcerpt>{useCase.excerpt}</CardExcerpt>
            <MetaRow>
              <span>{useCase.readingTime}</span>
            </MetaRow>
            <KeywordList>
              {useCase.keywords.slice(0, 3).map((keyword) => (
                <Keyword key={keyword}>{keyword}</Keyword>
              ))}
            </KeywordList>
          </Card>
        ))}
      </CardsGrid>
    </PageContainer>
  )
}

export default UseCaseIndex
